import { Injectable } from '@angular/core';
import { ArcgisEndpointConstant } from '@gishub/domain';
import { NowcastingKpiState } from './kpi-state';
import Polygon from '@arcgis/core/geometry/Polygon';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import * as unionOperator from '@arcgis/core/geometry/operators/unionOperator';

export namespace NowcastingKpiService {
  export interface ArcgisLayer {
    id: number;
    name: string;
    /** Host del servicio (MapServer/FeatureServer). */
    baseUrl: string;
    /**
     * Índice de subcapa dentro del servicio; la query usa
     * `${baseUrl}/${sublayer}/query`. NO usar `id`: no coinciden (el
     * catálogo 3 —Centros poblados— es MDE/5; el 6 —prod. agrícolas— es
     * temporal2/PADRON_GEO = 9).
     */
    sublayer: number;
    outFields: string[];
  }

  /** Subconjunto de la respuesta ArcGIS que consume el KPI. */
  export interface ArcgisQueryResponse {
    count?: number;
    features?: { attributes?: Record<string, unknown>; geometry?: unknown }[];
    /** OIDs que intersecan (respuesta de `returnIdsOnly`). */
    objectIds?: number[];
    /** true si el servidor cortó la respuesta y hay más registros (paginar). */
    exceededTransferLimit?: boolean;
    error?: unknown;
  }
}

/** Límite defensivo de páginas por consulta paginada (cota anti-bucle). */
const KPI_MAX_PAGES = 100;
/** Página por defecto del servidor si no reporta maxRecordCount. */
const KPI_FALLBACK_PAGE_SIZE = 1000;
/** Lote de `objectIds` por petición en la recuperación ID→features. */
const KPI_OBJECT_IDS_CHUNK = 200;
/** Techo absoluto de features recuperados por consulta (cota de memoria). */
const KPI_MAX_FEATURES_CEIL = 30000;

/** Campos del servicio de límites censales (única fuente: `LIMITE_FIELDS`). */
const F_DEP = ArcgisEndpointConstant.LIMITE_FIELDS.DEPARTAMENTO;
const F_PROV = ArcgisEndpointConstant.LIMITE_FIELDS.PROVINCIA;
const F_DIST = ArcgisEndpointConstant.LIMITE_FIELDS.DISTRITO;

/**
 * Campos de nombre que identifican la unidad administrativa en cada capa
 * territorial; su código INEI vive en `F_*.code` y se pide junto a ellos
 * en `outFields`.
 */
const TERRITORIAL_FIELDS: Record<number, string[]> = {
  0: [F_DEP.name],
  1: [F_DEP.name, F_PROV.name],
  2: [F_DEP.name, F_PROV.name, F_DIST.name],
};

/**
 * Clave única de la unidad: "DEP", "DEP|PROV" o "DEP|PROV|DIST".
 * Con la ubicación COMPLETA, un distrito homónimo de otra provincia no
 * colisiona. Null si no hay ningún campo de nombre informado.
 */
export function territorialKey(
  layerId: number,
  attrs: Record<string, unknown>,
): string | null {
  const parts: string[] = [];
  for (const f of TERRITORIAL_FIELDS[layerId] ?? []) {
    const v = attrs[f];
    parts.push(v == null ? '' : String(v).trim().toUpperCase());
  }
  if (parts.every((p) => p === '')) return null;
  return parts.join('|');
}

@Injectable()
export class NowcastingKpiService {
  /**
   * Catálogo de cruce KPI — SOLO Cartografía Base + Elementos expuestos.
   * Servicios y subcapas provienen de ArcgisEndpointConstant (única fuente);
   * `id` es solo clave interna del KPI y NO el índice de subcapa.
   * Baseline verificado en vivo contra producción: dep 25 · prov 196 ·
   * dist ~1.9k · CCPP 94 927 · parcelas 196 950 · prod. agrícolas
   * 2 854 205 · prod. pecuarios 4 871 616 · predios: solo con filtro
   * espacial, su count GLOBAL excede el timeout del servicio.
   */
  readonly layers: NowcastingKpiService.ArcgisLayer[] = [
    // ── Cartografía base (límites: SERV_LIMITE_CENSAL, CODDEP/CODPROV/UBIGEO) ──
    {
      id: 0,
      name: 'Departamentos',
      ...ArcgisEndpointConstant.LIMITE_DEPARTAMENTO,
      outFields: [F_DEP.code, F_DEP.name],
    },
    {
      id: 1,
      name: 'Provincias',
      ...ArcgisEndpointConstant.LIMITE_PROVINCIAL,
      outFields: [F_PROV.code, F_PROV.name, F_DEP.name],
    },
    {
      id: 2,
      name: 'Distritos',
      ...ArcgisEndpointConstant.LIMITE_DISTRITAL,
      outFields: [F_DIST.code, F_DIST.name, F_DEP.name, F_PROV.name],
    },
    // ── Elementos expuestos ──
    {
      id: 3,
      name: 'Centros poblados',
      ...ArcgisEndpointConstant.CENTROS_POBLADOS,
      outFields: ['NOMBCCPP', 'NOMBDIST', 'NOMBPROV', 'NOMBDEP'],
    },
    {
      id: 4,
      name: 'Predio rural catastral',
      ...ArcgisEndpointConstant.PREDIO_RURAL_CATASTRAL,
      outFields: ['*'],
    },
    {
      id: 5,
      name: 'Parcelas con cultivo',
      ...ArcgisEndpointConstant.PARCELAS_CULTIVO,
      outFields: ['*'],
    },
    {
      id: 6,
      name: 'Productores agrícolas empadronados',
      ...ArcgisEndpointConstant.PRODUCTORES_AGRICOLAS,
      outFields: ['*'],
    },
    {
      id: 7,
      name: 'Productores pecuarios empadronados',
      ...ArcgisEndpointConstant.PRODUCTORES_PECUARIOS,
      outFields: ['*'],
    },
    {
      id: 8,
      name: 'Puntos críticos priorizados',
      ...ArcgisEndpointConstant.PC_PUNTOS_CRITICOS_INICIAL_2026,
      outFields: [
        'SECTOR',
        'NOMBRE',
        'nombdep',
        'nombprov',
        'nombdist',
        'FUENTE_NATURAL',
        'HABITANTES',
        'VIVIENDAS',
        'AREA_CULTIVO',
        'PRESUPUESTO',
        'NIVEL_PELIGRO',
      ],
    },
  ];

  /**
   * IDs de capas prioritarias para el cálculo del KPI del Header y Panel Derecho:
   * 0: Departamentos (ancla territorial)
   * 5: Parcelas con cultivo (elemento expuesto)
   * 6: Productores agrícolas empadronados (elemento expuesto)
   * 7: Productores pecuarios empadronados (elemento expuesto)
   * 8: Puntos críticos priorizados (elemento expuesto / infraestructura y riesgo)
   */
  static readonly ACTIVE_LAYER_IDS: readonly number[] = [0, 5, 6, 7, 8];

  /** Catálogo filtrado únicamente a las capas activas de elementos expuestos y departamentos. */
  get activeLayers(): NowcastingKpiService.ArcgisLayer[] {
    return this.layers.filter((l) => NowcastingKpiService.ACTIVE_LAYER_IDS.includes(l.id));
  }

  /**
   * Pre-calcula y serializa la geometría unificada de alerta (dissolve)
   * una sola vez para reutilizarla entre todas las capas de ese nivel.
   */
  async prepareGeometryParam(rings: number[][][]): Promise<string> {
    return this.buildGeometryParam(rings);
  }

  /**
   * Cuenta elementos de la capa dentro de los anillos de alerta.
   * Devuelve null si el servicio falla (caído, timeout, subcapa inválida):
   * el llamador debe mostrar "sin dato" en vez de un 0 que parecería real.
   * Permite inyectar `prebuiltGeometry` y `whereClause` para máximo rendimiento.
   */
  async queryLayerCount(
    layerId: number,
    rings: number[][][],
    prebuiltGeometry?: string,
    whereClause = '1=1',
  ): Promise<number | null> {
    const geometry = prebuiltGeometry ?? (await this.buildGeometryParam(rings));
    const params = new URLSearchParams({
      where: whereClause,
      geometry,
      geometryType: 'esriGeometryPolygon',
      spatialRel: 'esriSpatialRelIntersects',
      inSR: '4326',
      outSR: '4326',
      returnGeometry: 'false',
      returnCountOnly: 'true',
      f: 'json',
    });

    const data = await this.queryJson(layerId, params);
    if (!data || data.error) return null;
    return data.count ?? 0;
  }

  /**
   * Unidades administrativas ÚNICAS que intersecan los anillos
   * (solo capas territoriales 0–2), como conjunto de claves
   * "DEP", "DEP|PROV" o "DEP|PROV|DIST".
   *
   * El conteo simple `returnCountOnly` duplica: un departamento que
   * interseca dos polígonos de niveles distintos (o dos del mismo
   * nivel) entra 2+ veces → "34 departamentos" en Perú. Aquí se piden
   * los campos de ubicación SIN geometría y se deduplica en cliente.
   * Devuelve null si el servicio falla: "sin dato", nunca 0.
   */
  async queryLayerUniqueKeys(
    layerId: number,
    rings: number[][][],
    prebuiltGeometry?: string,
    whereClause = '1=1',
    maxFeatures = 5000,
  ): Promise<Set<string> | null> {
    const fields = TERRITORIAL_FIELDS[layerId];
    if (!fields) return null;

    const geometry = prebuiltGeometry ?? (await this.buildGeometryParam(rings));
    const base = {
      where: whereClause,
      geometry,
      geometryType: 'esriGeometryPolygon',
      spatialRel: 'esriSpatialRelIntersects',
      inSR: '4326',
      outSR: '4326',
      returnGeometry: 'false',
      outFields: fields.join(','),
      f: 'json',
    };

    // Para la capa departamental (layer 0) solo hay 25 entidades en Perú:
    // 1 sola petición sin paginar devuelve todo el resultado.
    const effectiveLimit = layerId === 0 ? 50 : maxFeatures;
    const pages = await this.collectPages(layerId, base, effectiveLimit);
    if (!pages) return null;

    const keys = new Set<string>();
    for (const f of pages) {
      const key = territorialKey(layerId, f.attributes ?? {});
      if (key) keys.add(key);
    }
    return keys;
  }

  async queryLayerFeatures(
    layerId: number,
    rings: number[][][],
    outFields: string[],
    prebuiltGeometry?: string,
    whereClause = '1=1',
    maxFeatures = Infinity,
  ): Promise<{ features: NowcastingKpiState.LayerFeature[]; error: boolean }> {
    const geometry = prebuiltGeometry ?? (await this.buildGeometryParam(rings));
    const base = {
      where: whereClause,
      geometry,
      geometryType: 'esriGeometryPolygon',
      spatialRel: 'esriSpatialRelIntersects',
      inSR: '4326',
      outSR: '4326',
      returnGeometry: 'true',
      outFields: outFields.join(','),
      f: 'json',
    };

    // Vía robusta: IDs primero, features por `objectIds`. Los servicios Midagri
    // (p. ej. PADRON_GEO) no pagan bien las consultas espaciales —devuelven
    // 0/1/3 features con `resultRecordCount` aunque el conteo sea correcto—,
    // pero `returnIdsOnly` es fiable y la consulta por `objectIds` es exacta.
    const byId = await this.collectByObjectIds(layerId, base, maxFeatures);
    if (byId) {
      return {
        features: byId.map((f) => ({
          attributes: f?.attributes ?? {},
          geometry: f?.geometry ?? null,
        })),
        error: false,
      };
    }

    // Fallback clásico (paginación por offset) si el servicio no expone IDs.
    const pages = await this.collectPages(layerId, base, maxFeatures);
    if (!pages) return { features: [], error: true };

    return {
      features: pages.map((f) => ({
        attributes: f?.attributes ?? {},
        geometry: f?.geometry ?? null,
      })),
      error: false,
    };
  }

  /**
   * Recupera features en dos fases: (1) `returnIdsOnly` con el filtro espacial
   * → TODOS los OIDs que intersecan; (2) consulta por lotes de `objectIds`
   * (exacta y completa). Devuelve null si la fase 1 falla, para que el llamador
   * degrade a la paginación clásica. Un lote posterior fallido entrega lo
   * acumulado (degradación parcial, igual que `collectPages`).
   */
  private async collectByObjectIds(
    layerId: number,
    base: Record<string, string>,
    maxFeatures: number,
  ): Promise<NowcastingKpiService.ArcgisQueryResponse['features'] | null> {
    const idsData = await this.queryJson(
      layerId,
      new URLSearchParams({ ...base, returnGeometry: 'false', returnIdsOnly: 'true' }),
    );
    const ids = idsData?.objectIds;
    if (!ids) return null; // sin soporte de IDs: degradar a paginación
    if (ids.length === 0) return [];

    const limit = Number.isFinite(maxFeatures)
      ? maxFeatures
      : KPI_MAX_FEATURES_CEIL;
    const wanted = ids.slice(0, Math.max(0, limit));

    const all: NonNullable<NowcastingKpiService.ArcgisQueryResponse['features']> = [];
    for (let i = 0; i < wanted.length; i += KPI_OBJECT_IDS_CHUNK) {
      const chunk = wanted.slice(i, i + KPI_OBJECT_IDS_CHUNK);
      // La consulta por objectIds es exacta: no reenvía geometría ni `where`.
      const data = await this.queryJson(
        layerId,
        new URLSearchParams({
          objectIds: chunk.join(','),
          outFields: base.outFields,
          outSR: base.outSR,
          returnGeometry: 'true',
          f: 'json',
        }),
      );
      if (!data || data.error) {
        if (i === 0) return null; // fase 2 caída en el primer lote: degradar
        break; // lote intermedio caído: entregar lo acumulado
      }
      all.push(...(data.features ?? []));
    }
    return all;
  }

  /**
   * Consulta paginada: itera con resultOffset hasta agotar el resultado
   * (exceededTransferLimit) o alcanzar `maxFeatures`. Con `Infinity`
   * trae TODOS los registros — sin esto el servidor corta en
   * maxRecordCount y el usuario ve "1..de 15" sin poder ver el resto.
   * Devuelve null si la PRIMERA página falla (error real); una página
   * intermedia fallida entrega lo acumulado (degradación parcial).
   */
  private async collectPages(
    layerId: number,
    base: Record<string, string>,
    maxFeatures: number,
  ): Promise<NowcastingKpiService.ArcgisQueryResponse['features'] | null> {
    const all: NonNullable<NowcastingKpiService.ArcgisQueryResponse['features']> = [];
    const pageSize = await this.serverPageSize(layerId, base);
    let offset = 0;

    for (let page = 0; page < KPI_MAX_PAGES; page++) {
      const remaining = maxFeatures - all.length;
      if (remaining <= 0) break;
      const recordCount = Math.min(pageSize, remaining);

      const params = new URLSearchParams({
        ...base,
        resultRecordCount: String(recordCount),
        resultOffset: String(offset),
      });
      const data = await this.queryJson(layerId, params);
      if (!data || data.error) {
        if (page === 0) return null; // sin dato real, nunca un 0 fingido
        break; // página intermedia caída: degradar con lo acumulado
      }

      const batch = data.features ?? [];
      all.push(...batch);
      offset += batch.length;

      const serverSaysMore = data.exceededTransferLimit === true;
      if (batch.length < recordCount || (!serverSaysMore && batch.length > 0)) break;
    }
    return all;
  }

  /** Caché en memoria de maxRecordCount por subcapa (evita consultas HTTP repetidas a ?f=json). */
  private static readonly PAGE_SIZE_CACHE = new Map<number, number>();

  /**
   * maxRecordCount del servidor (1 consulta liviana cacheada): tamaño de página
   * real para paginar sin pedir de más. Fallback a 1000 si no expone.
   */
  private async serverPageSize(
    layerId: number,
    base: Record<string, string>,
  ): Promise<number> {
    const cached = NowcastingKpiService.PAGE_SIZE_CACHE.get(layerId);
    if (cached != null) return cached;

    const layer = this.layers.find((l) => l.id === layerId);
    if (!layer) return KPI_FALLBACK_PAGE_SIZE;
    try {
      const res = await fetch(`${layer.baseUrl}/${layer.sublayer}?f=json`);
      if (!res.ok) {
        NowcastingKpiService.PAGE_SIZE_CACHE.set(layerId, KPI_FALLBACK_PAGE_SIZE);
        return KPI_FALLBACK_PAGE_SIZE;
      }
      const meta = (await res.json()) as { maxRecordCount?: number };
      const size =
        meta.maxRecordCount && meta.maxRecordCount > 0
          ? meta.maxRecordCount
          : KPI_FALLBACK_PAGE_SIZE;
      NowcastingKpiService.PAGE_SIZE_CACHE.set(layerId, size);
      return size;
    } catch {
      NowcastingKpiService.PAGE_SIZE_CACHE.set(layerId, KPI_FALLBACK_PAGE_SIZE);
      return KPI_FALLBACK_PAGE_SIZE;
    }
  }

  /** Tamaño de lote para la unión incremental (evita O(n²) con cientos de anillos). */
  private static readonly UNION_CHUNK = 32;

  /**
   * Geometría de análisis para las queries: une (dissolve) los anillos de
   * alerta ANTES de consultar. Sin la unión, los anillos solapados viajan
   * como un multipart even-odd y toda zona cubierta por 2+ polígonos se
   * lee como hueco → el cruce devolvía 0 para todo (bug de producción:
   * 65 anillos N2 solapados ⇒ "0 departamentos afectados").
   * Si la unión falla, degrada a los anillos crudos (comportamiento previo).
   */
  private async buildGeometryParam(rings: number[][][]): Promise<string> {
    const unified = await this.unionRings(rings);
    return this.geometryParam(unified ?? rings);
  }

  /**
   * Une los anillos en polígonos disjuntos (exterior + huecos reales).
   * Devuelve null si no hay anillos válidos o si el operador falla.
   */
  private async unionRings(rings: number[][][]): Promise<number[][][] | null> {
    const usable = rings.filter(
      (r) => Array.isArray(r) && r.length >= 3,
    );
    if (usable.length === 0) return null;
    if (usable.length === 1) return usable;

    try {
      const sr = new SpatialReference({ wkid: 4326 });

      // Unión por lotes: executeMany sobre ~32 polígonos es lineal;
      // una sola llamada con miles sería cuadrática en memoria.
      const chunks: Polygon[] = [];
      for (let i = 0; i < usable.length; i += NowcastingKpiService.UNION_CHUNK) {
        const batch = usable
          .slice(i, i + NowcastingKpiService.UNION_CHUNK)
          .map((r) => new Polygon({ rings: [r], spatialReference: sr }));
        const u = unionOperator.executeMany(batch) as Polygon | null | undefined;
        if (u?.rings?.length) chunks.push(u);
      }
      if (chunks.length === 0) return null;

      const final =
        chunks.length > 1
          ? (unionOperator.executeMany(chunks) as Polygon | null | undefined)
          : chunks[0];
      if (!final?.rings?.length) return null;
      return final.rings as number[][][];
    } catch {
      return null; // sin unión → anillos crudos (even-odd, igual que antes)
    }
  }

  /** Serializa el polígono de intersección en el formato ArcGIS. */
  private geometryParam(rings: number[][][]): string {
    return JSON.stringify({ rings, spatialReference: { wkid: 4326 } });
  }

  /**
   * POST de query ArcGIS con parsing tipado. Devuelve null si la red,
   * el HTTP status o el JSON fallan; el llamador decide el fallback.
   */
  private async queryJson(
    layerId: number,
    params: URLSearchParams,
  ): Promise<NowcastingKpiService.ArcgisQueryResponse | null> {
    const layer = this.layers.find((l) => l.id === layerId);
    if (!layer) return null;
    try {
      const res = await fetch(
        `${layer.baseUrl}/${layer.sublayer}/query`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: params.toString(),
        },
      );
      if (!res.ok) return null;
      return (await res.json()) as NowcastingKpiService.ArcgisQueryResponse;
    } catch {
      return null;
    }
  }
}
